/**
 * Trend Tracker domain model + pure helpers.
 *
 * Mirrors the Supabase schema in the Trend Tracker Feature Recipe: trends,
 * trend_signals, trend_snapshots, team_trend_relevance. Shared with the
 * backend so the FE can swap the fixture for real API responses without
 * touching components.
 */

export type TrendCategory = 'social' | 'creator' | 'content' | 'marketing';
export type TrendStatus = 'emerging' | 'active' | 'peaking' | 'declining';
export type TrendOriginPlatform =
  | 'tiktok'
  | 'instagram'
  | 'youtube'
  | 'reddit'
  | 'cross-platform'
  | 'meta-ads'
  | 'tiktok-ads';
export type TrendSignalSource =
  | 'beatly_mentions'
  | 'google_trends'
  | 'google_trends_now'
  | 'google_news'
  | 'tiktok_cc'
  | 'meta_ad_library'
  | 'reddit'
  | 'ad_anomaly'
  | 'youtube'
  | 'newsapi';
export type TrendSignalType =
  | 'hashtag_spike'
  | 'search_volume'
  | 'engagement_anomaly'
  | 'viral_post'
  | 'keyword_velocity';
export type TrendOpportunity = 'high' | 'medium' | 'low';

/**
 * Typed strategic angle emitted by the Gemini synthesis pass. Each trend carries
 * 3-5 of these, covering different lenses the brand could use: audience insight,
 * content concept, creator strategy, brand positioning, timing plan, channel mix.
 * Renders as a separate card in the drawer so the marketer sees multiple moves
 * for one trend, not one catch-all paragraph.
 */
export type TrendAngleType =
  | 'audience'
  | 'content_concept'
  | 'creator_strategy'
  | 'brand_strategy'
  | 'timing'
  | 'channel_mix';

export interface TrendAngle {
  type: TrendAngleType;
  title: string;
  detail: string;
}

export interface TrendSnapshot {
  /** YYYY-MM-DD */
  snapshot_date: string;
  virality_score: number;
  mention_count: number;
  search_volume: number;
}

export interface TrendSignal {
  id: string;
  source: TrendSignalSource;
  signal_type: TrendSignalType;
  raw_data: {
    keyword?: string;
    hashtag?: string;
    sample_post_url?: string;
    sample_caption?: string;
    velocity_ratio?: number;
    z_score?: number;
    [k: string]: unknown;
  };
  geo?: string | null;
  recorded_at: string;
}

export interface TeamTrendRelevance {
  relevance_score: number; // 0-100
  reasoning: string;
  suggested_action: string;
  dismissed: boolean;
}

export interface Trend {
  id: string;
  slug: string;
  title: string;
  description: string;
  why_it_matters: string;
  category: TrendCategory;
  origin_platform: TrendOriginPlatform;
  virality_score: number;
  status: TrendStatus;
  geo_scope: string;
  first_seen_at: string;
  last_updated: string;
  metadata: {
    hashtags?: string[];
    related_keywords?: string[];
    sample_posts?: string[];
    cover_image_url?: string | null;
    /** Gemini's classification: 'moment' = specific named event with 48–72h activation
     *  window; 'theme' = broader pattern with multi-week runway. Used by the FE to
     *  surface a distinct pill / filter. */
    moment_or_theme?: 'moment' | 'theme';
    /** Populated for moment trends: canonical TikTok share URL for the dominant hashtag's
     *  top video. Safe to use as a click-through target. */
    cover_video_url?: string | null;
    /** TikTok embed-ready URL (iframe player). Preferred for in-app playback over
     *  share_url when rendering inside the drawer. */
    cover_embed_url?: string | null;
    /** Provenance of cover_image_url. 'stock' = category default; 'tiktok' = oEmbed
     *  thumbnail from the top video; 'google_images' = SerpAPI google_images result. */
    cover_source?: 'stock' | 'tiktok' | 'google_images';
    /** Typed strategic angles — 3-5 per trend, each a distinct lens (audience,
     *  content concept, creator strategy, brand positioning, timing, channel mix).
     *  Replaces the single catch-all suggested_action when present; the scalar
     *  suggested_action field stays populated for card-preview compatibility. */
    angles?: TrendAngle[];
  };
  signals: TrendSignal[];
  snapshots: TrendSnapshot[];
  relevance: TeamTrendRelevance;
}

/**
 * Opportunity rating — derived classifier a marketer actually wants to skim.
 * We translate the two raw scores (virality, relevance) into a one-word call.
 * High = both are strong; Low = both are weak; Medium = mixed signals.
 */
export function opportunityFor(trend: Trend): TrendOpportunity {
  const v = trend.virality_score;
  const r = trend.relevance.relevance_score;
  if (v >= 80 && r >= 75) return 'high';
  if (v >= 60 && r >= 60) return 'medium';
  return 'low';
}

/**
 * Virality score from raw signals. Weighs signal count, source diversity,
 * and the strongest velocity hit. Output clamped to 0-100.
 */
export function computeViralityScore(signals: TrendSignal[]): number {
  if (signals.length === 0) return 0;
  const countWeight = Math.min(signals.length / 10, 1) * 40;
  const sources = new Set(signals.map((s) => s.source));
  const diversityWeight = Math.min(sources.size / 4, 1) * 30;
  const peakVelocity = signals.reduce((max, s) => {
    const v = (s.raw_data.velocity_ratio as number | undefined) ?? 0;
    const z = Math.abs((s.raw_data.z_score as number | undefined) ?? 0);
    return Math.max(max, v, z / 2);
  }, 0);
  const velocityWeight = Math.min(peakVelocity / 5, 1) * 30;
  return Math.round(countWeight + diversityWeight + velocityWeight);
}

/**
 * Lifecycle classifier based on snapshot trajectory over the last 7d vs the
 * previous 7d. Pure and testable — the real pipeline can feed an explicit
 * status, but this lets the UI derive one from snapshots alone.
 */
export function classifyStatus(snapshots: TrendSnapshot[]): TrendStatus {
  if (snapshots.length < 3) return 'emerging';
  const sorted = [...snapshots].sort((a, b) => (a.snapshot_date < b.snapshot_date ? -1 : 1));
  const recent = sorted.slice(-7);
  const previous = sorted.slice(-14, -7);
  if (recent.length === 0) return 'emerging';
  const avg = (arr: TrendSnapshot[]) =>
    arr.reduce((a, b) => a + (b.virality_score ?? 0), 0) / Math.max(arr.length, 1);
  const recentAvg = avg(recent);
  const prevAvg = avg(previous);
  if (prevAvg === 0 && recentAvg > 0) return 'emerging';
  const delta = recentAvg - prevAvg;
  if (delta > prevAvg * 0.2) return 'emerging';
  if (delta < -prevAvg * 0.2) return 'declining';
  if (recentAvg > 80) return 'peaking';
  return 'active';
}

export function scoreOpportunityOrder(a: Trend, b: Trend): number {
  // Relevance outweighs virality in the feed — we're showing THIS team's story.
  const keyA = a.relevance.relevance_score * 0.7 + a.virality_score * 0.3;
  const keyB = b.relevance.relevance_score * 0.7 + b.virality_score * 0.3;
  return keyB - keyA;
}

/**
 * Does this trend match a free-text query? Tokenised (whitespace-split) AND-match
 * over: title, description, why_it_matters, each hashtag / related keyword, and
 * every signal's keyword/hashtag. Case-insensitive. Returns true for empty query.
 *
 * Kept pure so TrendsPage and TrendsForYouWidget share the same semantics.
 */
export function trendMatchesQuery(trend: Trend, query: string): boolean {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const haystackParts: string[] = [
    trend.title,
    trend.description,
    trend.why_it_matters,
    ...(trend.metadata.hashtags ?? []),
    ...(trend.metadata.related_keywords ?? []),
  ];
  for (const s of trend.signals) {
    const kw = s.raw_data?.keyword;
    const ht = s.raw_data?.hashtag;
    if (typeof kw === 'string') haystackParts.push(kw);
    if (typeof ht === 'string') haystackParts.push(ht);
  }
  const haystack = haystackParts.join(' \n ').toLowerCase();
  return tokens.every((t) => haystack.includes(t));
}

/**
 * A trend's `geo_scope` may be a single ISO code ('SE'), the literal 'global',
 * or a comma-joined string ('DE, GB, NO') when Gemini ties one trend to several
 * markets. Returns true if the filter value matches any of those, with 'all' and
 * 'global' passing through everything.
 */
export function trendMatchesGeo(trend: Trend, geo: string): boolean {
  if (!geo || geo === 'all') return true;
  const scope = (trend.geo_scope || '').trim();
  if (!scope || scope === 'global') return true;
  if (geo === 'global') return true;
  const tokens = scope.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
  return tokens.includes(geo.toUpperCase());
}

/**
 * Raw top-list rankings per source, saved by the generator alongside the
 * Gemini-synthesized `trends`. Powers the "Trend Rankings" page — same fixture
 * fetch, different presentation.
 */
export interface TrendRankingsTikTokHashtag {
  hashtag: string;
  country: string;
  rank_position: number | null;
  rank_change: number;
  posts: number;
  posts_global_lifetime: number;
  velocity_ratio: number;
  top_country_list: string[];
  /** 30-day daily views from TikTok Discovery API — newest last. Powers the
   *  inline sparkline in the rankings UI. May be empty if the API returned
   *  no history for this hashtag. */
  views_daily_series: number[];
  /** Peak daily views in the last 7 days — quick "how big did it get" stat. */
  views_7d_peak: number;
}
export interface TrendRankingsGoogleTrendsItem {
  keyword: string;
  country: string;
  traffic: number;
  velocity_ratio: number;
  sample_headline: string | null;
}
export interface TrendRankingsNewsItem {
  keyword: string;
  title: string;
  source: string | null;
  published: string | null;
  url: string | null;
  country: string | null;
}
export interface TrendRankingsYouTubeItem {
  keyword: string;
  title: string;
  channel: string | null;
  published: string | null;
  duration: string | null;
  url: string | null;
}
export interface TrendRankingsRedditItem {
  title: string;
  subreddit: string | null;
  score: number;
  comments: number;
  url: string | null;
}
export interface TrendRankings {
  tiktok_hashtags: TrendRankingsTikTokHashtag[];
  google_trends_now: TrendRankingsGoogleTrendsItem[];
  google_news: TrendRankingsNewsItem[];
  youtube: TrendRankingsYouTubeItem[];
  reddit: TrendRankingsRedditItem[];
}

export function formatVelocity(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  const pct = (ratio - 1) * 100;
  if (pct >= 0) return `+${Math.round(pct)}%`;
  return `${Math.round(pct)}%`;
}
